// ABOUTME: Authentication context providing auth state and methods
// ABOUTME: Wraps app to provide useAuth hook globally

import React, { createContext, useState, useEffect, useMemo, ReactNode } from 'react'
import { authApi } from '@/api/auth'
import type { AuthState, AuthContextType } from '@/types/auth'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: localStorage.getItem('access_token'),
    isLoading: true,
  })

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token')

      if (!token) {
        setAuthState(prev => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const user = await authApi.me()
        setAuthState({
          isAuthenticated: true,
          user,
          token,
          isLoading: false,
        })
      } catch (error) {
        // Token invalid, clear it
        localStorage.removeItem('access_token')
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
        })
      }
    }

    checkAuth()
  }, [])

  // Listen for 401 unauthorized events
  useEffect(() => {
    const handleUnauthorized = () => {
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
      })
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [])

  const login = async (username: string, password: string) => {
    const response = await authApi.login({ username, password })

    // Fetch user details
    const user = await authApi.me()

    setAuthState({
      isAuthenticated: true,
      user,
      token: response.access_token,
      isLoading: false,
    })
  }

  const logout = async () => {
    await authApi.logout()
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
    })
  }

  const refreshAuth = async () => {
    try {
      const user = await authApi.me()
      setAuthState(prev => ({ ...prev, user }))
    } catch (error) {
      logout()
    }
  }

  // CRITICAL: Memoize value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      ...authState,
      login,
      logout,
      refreshAuth,
    }),
    [authState]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
